/** Copyright 2026 Midas Healthcare Solutions - All Rights Reserved **/
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  signal,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { ImageCropperComponent, ImageCroppedEvent } from 'ngx-image-cropper';
import { MatInputModule } from '@angular/material/input';
import { switchMap, take, tap } from 'rxjs';
import { ApiService } from '@midas/shared/common';
import { StorageService, UserActions, UserSelectors } from '@midas/shared/data-access';
import { Store } from '@ngrx/store';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'midas-profile-picture-dialog',
  templateUrl: './profile-picture-dialog.component.html',
  styleUrls: ['./profile-picture-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    DragDropModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    TranslocoModule,
    ImageCropperComponent,
  ],
})
export class ProfilePictureDialogComponent implements OnInit, OnDestroy {
  @ViewChild('videoEl') videoEl?: ElementRef<HTMLVideoElement>;
  @ViewChild('canvasEl') canvasEl?: ElementRef<HTMLCanvasElement>;
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  mode = signal<'loading' | 'camera' | 'crop'>('loading');
  errorMessage: string | null = null;
  isFirstLogin = false;
  isStartingCamera = false;
  isSaving = false;

  imageChangedEvent: Event | null = null;
  imageBase64: string | undefined;

  croppedBlob: Blob | null = null;
  croppedPreviewUrl: string | null = null;

  private mediaStream: MediaStream | null = null;
  private objectUrl: string | null = null;

  constructor(
    private api: ApiService,
    private change: ChangeDetectorRef,
    private storage: StorageService,
    private store: Store,
    private transloco: TranslocoService,
    public dialogRef: MatDialogRef<ProfilePictureDialogComponent>,
  ) {}

  ngOnInit(): void {
    this.store
      .select(UserSelectors.getUserState)
      .pipe(take(1))
      .subscribe((user) => (this.isFirstLogin = !user.hasLoggedIntoAlibi));

    this.startCamera();
  }

  ngOnDestroy(): void {
    this.stopCamera();
    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
    }
  }

  async startCamera() {
    this.stopCamera();
    this.resetCropState();
    this.errorMessage = null;
    this.isStartingCamera = true;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
        audio: false,
      });

      this.mediaStream = stream;
      this.mode.set('camera');
      this.change.detectChanges();

      const video = this.videoEl?.nativeElement;
      if (!video) return;
      video.srcObject = stream;
      await video.play();
    } catch {
      this.errorMessage = this.transloco.translate('ProfilePicCameraError');
      this.mode.set('loading');
    } finally {
      this.isStartingCamera = false;
      this.change.markForCheck();
    }
  }

  stopCamera(): void {
    if (this.videoEl?.nativeElement) {
      this.videoEl.nativeElement.pause();
      this.videoEl.nativeElement.srcObject = null;
    }

    if (this.mediaStream) {
      this.mediaStream.getTracks().forEach((track) => track.stop());
      this.mediaStream = null;
    }
  }

  capturePhoto(): void {
    const video = this.videoEl?.nativeElement;
    const canvas = this.canvasEl?.nativeElement;

    if (!video || !canvas) return;

    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;

    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.drawImage(video, 0, 0, width, height);

    const dataUrl = canvas.toDataURL('image/jpeg', 1);
    this.imageBase64 = dataUrl;
    this.imageChangedEvent = null;
    this.mode.set('crop');
    this.stopCamera();
  }

  onFileSelected(event: Event): void {
    this.stopCamera();

    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }

    this.croppedBlob = null;
    this.croppedPreviewUrl = null;
    this.imageBase64 = undefined;
    this.errorMessage = null;

    this.imageChangedEvent = event;
    this.mode.set('crop');
  }

  imageCropped(event: ImageCroppedEvent): void {
    this.croppedBlob = event.blob ?? null;

    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }

    if (this.croppedBlob) {
      this.objectUrl = URL.createObjectURL(this.croppedBlob);
      this.croppedPreviewUrl = this.objectUrl;
    } else {
      this.croppedPreviewUrl = null;
    }

    this.change.detectChanges();
  }

  loadImageFailed(): void {
    this.resetAll();
    this.errorMessage = this.transloco.translate('ProfilePicImageLoadError');
  }

  async save() {
    if (!this.croppedBlob) {
      this.errorMessage = this.transloco.translate('ProfilePicSavingError');
      return;
    }

    this.isSaving = true;

    try {
      const file = new File([this.croppedBlob], 'pfp.jpg', {
        type: this.croppedBlob.type || 'image/jpeg',
      });

      this.api
        .get<{ url: string }>('/user/profile-picture/file-upload-url')
        .pipe(
          switchMap((uploadUrlResponse) =>
            this.storage
              .upload(uploadUrlResponse.url, file)
              .pipe(tap(() => this.store.dispatch(UserActions.setProfilePictureUploadComplete()))),
          ),
        )
        .subscribe({
          next: () => this.close(),
          error: (e) => (this.errorMessage = e),
        });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      this.errorMessage = e;
    } finally {
      this.isSaving = false;
    }
  }

  openFilePicker(): void {
    this.fileInput?.nativeElement.click();
  }

  retake(): void {
    this.resetAll();
    this.startCamera();
  }

  private resetCropState(): void {
    this.imageChangedEvent = null;
    this.imageBase64 = undefined;
    this.croppedBlob = null;
    this.croppedPreviewUrl = null;

    if (this.objectUrl) {
      URL.revokeObjectURL(this.objectUrl);
      this.objectUrl = null;
    }

    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  resetAll(): void {
    this.stopCamera();
    this.resetCropState();
    this.errorMessage = null;
    this.mode.set('loading');
  }

  close() {
    this.resetAll();
    this.dialogRef.close();
  }
}
